<template>
	<v-card v-if="podeVer">
		<v-card-title>
			Recebimento de Pix do Itaú
			<v-spacer />
			<v-btn small outlined color="primary" :loading="carregandoStatus" @click="carregarTudo">
				<v-icon left small>mdi-refresh</v-icon>Atualizar
			</v-btn>
		</v-card-title>
		<v-divider />
		<v-card-text class="pb-0">
			<div class="text-body-2 grey--text text--darken-1 mb-2">
				Captura os pagamentos feitos pelo QR Code Pix do boleto (o aviso de cobrança do banco não os envia) e os leva para a Conciliação. A baixa segue a baixa automática por filial do boleto: com ela ligada, o Pix recebido na hora é baixado sozinho; sem ela, a baixa é feita pelo botão da Conciliação.
			</div>
			<v-alert v-if="status && !status.disponivel && !carregandoStatus" dense outlined type="error" class="mb-3">
				As tabelas do Pix ainda não existem. Rode o SQL pix-tabelas.sql no DB2.
			</v-alert>
			<div v-if="status && status.disponivel" class="mb-2">
				<v-chip small class="mr-2 mb-1" :color="status.filiaisativas ? 'green' : 'grey'" text-color="white">
					{{ status.filiaisativas }} de {{ status.filiaiscadastradas }} filial(is) ligada(s)
				</v-chip>
				<v-chip small class="mr-2 mb-1" outlined>Pix nas últimas 24h: {{ status.pix24h }} ({{ status.bolecode24h }} de boleto)</v-chip>
				<v-chip small class="mr-2 mb-1" outlined>Último Pix: {{ status.ultimopix || "-" }}</v-chip>
				<v-chip small class="mr-2 mb-1" :color="status.erros24h ? 'red' : 'green'" text-color="white">Erros em 24h: {{ status.erros24h }}</v-chip>
				<v-chip small class="mr-2 mb-1" :color="status.intervalominutos ? undefined : 'orange'" :text-color="status.intervalominutos ? undefined : 'white'" :outlined="!!status.intervalominutos">
					{{ status.intervalominutos ? "Consulta a cada " + status.intervalominutos + " min" : "Agendamento desligado (cronjob)" }}
				</v-chip>
			</div>
		</v-card-text>
		<v-tabs v-model="aba" show-arrows>
			<v-tab>Filiais</v-tab>
			<v-tab>Atividade</v-tab>
			<v-tab>Consulta e testes</v-tab>
			<v-tab>Webhook</v-tab>
		</v-tabs>
		<v-divider />

		<v-tabs-items v-model="aba">
			<!-- Filiais -->
			<v-tab-item>
				<v-card-text>
					<v-alert v-if="!filialPix.disponivel && !carregandoFilial" dense outlined type="error" class="mb-3">
						A tabela BOLETOS.TB_PIX_FILIAL ainda não existe. Rode o SQL pix-tabelas.sql no DB2.
					</v-alert>
					<v-simple-table dense>
						<thead>
							<tr>
								<th>Filial</th>
								<th>Recebimento de Pix</th>
								<th>Chave Pix</th>
								<th>Implantação</th>
								<th>Última consulta</th>
								<th>Último sucesso</th>
								<th>Pix 24h / 7d</th>
								<th>Situação</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="f in linhasFilial" :key="f.idempresa">
								<td>{{ f.rotulo }}</td>
								<td>
									<v-switch
										:key="`${f.idempresa}-${versaoSwitch}`"
										:input-value="f.ativa"
										color="green"
										dense
										hide-details
										class="mt-0 pt-0"
										:disabled="!podeGerenciar || !filialPix.disponivel || salvando === f.idempresa"
										:label="f.ativa ? 'Ligado' : 'Desligado'"
										@change="pedirAlterar(f, $event)"
									/>
								</td>
								<td>
									<span class="mono">{{ f.chavepix || f.chavedetectada || f.chavepadrao || "-" }}</span>
									<v-tooltip v-if="!f.chavepix && f.chavedetectada" bottom max-width="300">
										<template v-slot:activator="{ on }">
											<v-chip x-small class="ml-1" v-on="on">detectada</v-chip>
										</template>
										<span>Informada pelo Itaú nos Pix recebidos.</span>
									</v-tooltip>
									<v-tooltip v-else-if="!f.chavepix && f.chavepadrao" bottom max-width="300">
										<template v-slot:activator="{ on }">
											<v-chip x-small class="ml-1" v-on="on">CNPJ</v-chip>
										</template>
										<span>Chave padrão: o CNPJ da filial. Confirme no teste de autenticação.</span>
									</v-tooltip>
									<v-tooltip v-if="!f.chavepix && f.chavedetectada && f.chavepadrao && f.chavedetectada !== f.chavepadrao" bottom max-width="300">
										<template v-slot:activator="{ on }">
											<v-icon x-small color="orange" class="ml-1" v-on="on">mdi-alert-outline</v-icon>
										</template>
										<span>A chave informada pelo Itaú é diferente do CNPJ da filial ({{ f.chavepadrao }}).</span>
									</v-tooltip>
									<v-btn v-if="podeGerenciar && filialPix.disponivel" icon x-small class="ml-1" title="Informar a chave Pix (cria o cadastro da filial, ainda desligado)" @click="abrirChave(f)">
										<v-icon x-small>mdi-pencil</v-icon>
									</v-btn>
								</td>
								<td>{{ f.dtimplantacao ? formatarDia(f.dtimplantacao) : "sem data (últimas 48h)" }}</td>
								<td>{{ f.dtultvarredura || "-" }}</td>
								<td>{{ f.dtultsucesso || "-" }}</td>
								<td>{{ f.pix24h }} / {{ f.pix7d }} <span class="grey--text">({{ f.bolecode7d }} de boleto em 7d)</span></td>
								<td>
									<v-chip v-if="f.emexecucao" x-small color="blue" text-color="white">executando</v-chip>
									<v-chip v-else-if="f.errosseguidos > 0" x-small color="red" text-color="white" :title="f.ulterro">
										{{ f.errosseguidos }} erro(s) seguido(s)
									</v-chip>
									<v-chip v-else-if="f.ativa" x-small color="green" text-color="white">ok</v-chip>
								</td>
							</tr>
							<tr v-if="!linhasFilial.length">
								<td colspan="8" class="grey--text">Nenhuma filial carregada.</td>
							</tr>
						</tbody>
					</v-simple-table>
					<div class="text-caption grey--text mt-3">
						Toda filial começa desligada. A data de implantação é a mesma da Conciliação (TB_WEBHOOK_BAIXA_FILIAL): a primeira consulta começa nela, o que faz a carga retroativa. Recomenda-se simular o período na aba Consulta e testes antes de ligar. Cada alteração fica registrada na aba Atividade (tipo Configuração do Pix).
					</div>
				</v-card-text>
			</v-tab-item>

			<!-- Atividade -->
			<v-tab-item>
				<v-card-text>
					<v-row dense align="center">
						<v-col cols="12" sm="3">
							<v-select v-model="tipoLog" :items="opcoesTipo" label="Tipo" outlined dense hide-details @change="carregarLog" />
						</v-col>
						<v-col cols="12" sm="3">
							<v-select v-model="filialLog" :items="opcoesFiliaisTodas" label="Filial" outlined dense hide-details @change="carregarLog" />
						</v-col>
						<v-col cols="12" sm="2">
							<v-select v-model="limiteLog" :items="[50, 100, 200, 500]" label="Linhas" outlined dense hide-details @change="carregarLog" />
						</v-col>
						<v-col cols="12" sm="4" class="d-flex align-center">
							<v-btn small outlined color="primary" class="mr-3" :loading="carregandoLog" @click="carregarLog">Atualizar</v-btn>
							<v-switch v-model="autoAtualizar" dense hide-details label="Atualizar a cada 15s" class="mt-0" @change="configurarAutoAtualizar" />
						</v-col>
					</v-row>
					<v-data-table
						class="mt-3"
						dense
						:headers="headersLog"
						:items="log"
						:items-per-page="25"
						no-data-text="Nenhum registro para esse filtro"
						:loading="carregandoLog"
						@click:row="abrirDetalhe"
					>
						<template v-slot:item.dtlog="{ item }">{{ formatarData(item.dtlog) }}</template>
						<template v-slot:item.tipo="{ item }">
							<v-chip x-small :color="corTipo(item.tipo)" text-color="white">{{ rotuloTipo(item.tipo) }}</v-chip>
						</template>
						<template v-slot:item.statuscode="{ item }">
							<v-chip x-small :color="corStatus(item.statuscode)" text-color="white">{{ item.statuscode || "-" }}</v-chip>
						</template>
						<template v-slot:item.resposta="{ item }">
							<span class="texto-curto">{{ item.resposta }}</span>
						</template>
					</v-data-table>
					<div class="text-caption grey--text mt-2">Clique numa linha para ver o texto completo. O nome e o texto livre do pagador nunca são gravados no log.</div>
				</v-card-text>
			</v-tab-item>

			<!-- Consulta e testes -->
			<v-tab-item>
				<v-card-text>
					<v-row dense align="center">
						<v-col cols="12" sm="4">
							<v-select v-model="filialConsulta" :items="opcoesFiliais" label="Filial" outlined dense hide-details />
						</v-col>
						<v-col cols="12" sm="8">
							<v-btn small outlined color="primary" class="mr-2" :disabled="!filialConsulta || !podeExecutar" :loading="testando" @click="testar">
								Testar autenticação e consulta (última hora)
							</v-btn>
						</v-col>
					</v-row>
					<v-alert v-if="resultadoTeste" dense text :type="resultadoTeste.autenticacao ? 'success' : 'warning'" class="mt-3 mb-0">
						{{ resultadoTeste.mensagem }}
						<div v-if="resultadoTeste.chaveconsultada" class="text-caption mt-1">
							Webhook cadastrado na chave {{ resultadoTeste.chaveconsultada }}: HTTP {{ resultadoTeste.webhookstatus || "-" }}
							<span v-if="resultadoTeste.webhookstatus === 404 || resultadoTeste.webhookstatus === 204">(chave livre, sem webhook de outro sistema)</span>
							<span v-else-if="resultadoTeste.webhookstatus === 200">(já existe um webhook nesta chave: não cadastre o nosso sem alinhar com quem o usa)</span>
						</div>
					</v-alert>

					<v-divider class="my-4" />
					<div class="text-subtitle-2 mb-2">Consultar um período</div>
					<v-row dense align="center">
						<v-col cols="12" sm="3">
							<v-text-field v-model="periodo.inicio" label="Início" type="datetime-local" outlined dense hide-details />
						</v-col>
						<v-col cols="12" sm="3">
							<v-text-field v-model="periodo.fim" label="Fim" type="datetime-local" outlined dense hide-details />
						</v-col>
						<v-col cols="12" sm="3">
							<v-switch v-model="periodo.simular" dense hide-details class="mt-0" :label="periodo.simular ? 'Simular (não grava nada)' : 'Gravar na Conciliação'" />
						</v-col>
						<v-col cols="12" sm="3">
							<v-btn small color="primary" elevation="0" :disabled="!filialConsulta || !podeExecutar" :loading="consultando" @click="pedirConsulta">Consultar</v-btn>
						</v-col>
					</v-row>
					<div class="text-caption grey--text mt-1">O período máximo é de 31 dias. Simular consulta o Itaú e mostra o que seria feito, sem gravar nada e sem precisar ligar a filial.</div>

					<div v-if="resultado" class="mt-4">
						<v-alert v-if="resultado.aviso" dense text type="warning">{{ resultado.aviso }}</v-alert>
						<div class="mb-2">
							<v-chip small class="mr-2 mb-1" outlined>{{ resultado.simulado ? "Simulação" : "Gravado" }} de {{ resultado.inicio }} a {{ resultado.fim }}</v-chip>
							<v-chip
								small
								class="mr-2 mb-1"
								:outlined="!!filtroTipo"
								:color="filtroTipo ? undefined : 'primary'"
								:text-color="filtroTipo ? undefined : 'white'"
								title="Clique para mostrar todos"
								@click="filtroTipo = null"
							>
								Consultados: {{ resultado.consultados }}
							</v-chip>
							<v-chip
								v-for="(q, tipo) in resultado.portipo"
								:key="tipo"
								small
								class="mr-2 mb-1"
								:outlined="filtroTipo !== tipo"
								:color="filtroTipo === tipo ? 'primary' : undefined"
								:text-color="filtroTipo === tipo ? 'white' : undefined"
								title="Clique para filtrar a tabela por este tipo"
								@click="filtrarTipo(tipo)"
							>
								{{ rotuloTipoPix(tipo) }}: {{ q }}
							</v-chip>
							<template v-if="!resultado.simulado">
								<v-chip small class="mr-2 mb-1" color="teal" text-color="white">Novos: {{ resultado.novos }}</v-chip>
								<v-chip small class="mr-2 mb-1" color="teal" text-color="white">Na Conciliação: {{ resultado.promovidos }}</v-chip>
								<v-chip small class="mr-2 mb-1" color="orange" text-color="white">Divergentes: {{ resultado.divergentes }}</v-chip>
							</template>
							<v-chip v-if="resultado.invalidos" small class="mr-2 mb-1" color="red" text-color="white">Recusados na validação: {{ resultado.invalidos }}</v-chip>
						</div>
						<div v-if="filtroTipo" class="text-caption grey--text mb-1">
							Mostrando {{ itensFiltrados.length }} de {{ resultado.itens.length }} na lista. Clique em "Consultados" para ver todos.
						</div>
						<v-simple-table v-if="resultado.itens && resultado.itens.length" dense>
							<thead>
								<tr>
									<th>Horário</th>
									<th>Tipo</th>
									<th>Nosso número</th>
									<th class="text-right">Valor</th>
									<th>Situação prevista</th>
									<th>txid</th>
									<th>endToEndId</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="i in itensFiltrados" :key="i.endtoend">
									<td class="text-no-wrap">{{ i.horario }}</td>
									<td>{{ rotuloTipoPix(i.tipo) }}</td>
									<td class="mono">{{ i.nosso || "-" }}</td>
									<td class="text-right mono">{{ formataDinheiro(i.valor) }}</td>
									<td>
										<span v-if="i.situacao">{{ i.situacao }}</span>
										<span v-else-if="i.tipo !== 'BOLECODE'" class="grey--text">não é de boleto</span>
										<span v-if="i.motivo" class="grey--text"> ({{ i.motivo }})</span>
									</td>
									<td class="mono texto-curto" :title="i.txid">{{ i.txid || "-" }}</td>
									<td class="mono texto-curto">{{ i.endtoend }}</td>
								</tr>
							</tbody>
						</v-simple-table>
					</div>
				</v-card-text>
			</v-tab-item>

			<!-- Webhook -->
			<v-tab-item>
				<v-card-text>
					<div class="text-body-2 grey--text text--darken-1 mb-3">
						O webhook avisa o Pix em segundos. Cada chave Pix aceita uma única URL: cadastrar o nosso substitui o de outro sistema (por exemplo o do PDV). O Itaú só avisa Pix recebidos pela chave; transferência digitada por agência e conta só aparece na consulta periódica, que continua ligada como segurança.
					</div>
					<v-row dense align="center">
						<v-col cols="12" sm="4">
							<v-select v-model="filialConsulta" :items="opcoesFiliais" label="Filial" outlined dense hide-details />
						</v-col>
						<v-col cols="12" sm="8">
							<v-btn small outlined color="indigo" class="mr-2 mb-1" :disabled="!filialConsulta" :loading="consultandoWebhook" @click="consultarWebhook">Consultar cadastro no Itaú</v-btn>
							<v-btn small outlined color="green darken-2" class="mr-2 mb-1" :disabled="!filialConsulta || !podeWebhook" @click="dialogCadastrarWebhook = true">Cadastrar nosso webhook</v-btn>
							<v-btn small outlined color="red darken-2" class="mb-1" :disabled="!filialConsulta || !podeWebhook" @click="dialogRemoverWebhook = true">Remover</v-btn>
						</v-col>
					</v-row>
					<v-alert v-if="resultadoWebhook" dense text :type="tipoAlertaWebhook" class="mt-3">
						<div>{{ explicacaoWebhook }}</div>
						<div class="text-caption mt-1">
							Chave: <span class="mono">{{ resultadoWebhook.chave || "-" }}</span> | HTTP {{ resultadoWebhook.status || "-" }}
						</div>
						<div class="text-caption">Endereço que seria cadastrado: <span class="mono">{{ resultadoWebhook.urlnossa || "(PIX_WEBHOOK_URL_BASE não configurado no servidor)" }}</span></div>
						<pre v-if="resultadoWebhook.resposta" class="bloco-codigo mt-2">{{ resultadoWebhook.resposta }}</pre>
					</v-alert>
					<div class="text-caption grey--text mt-3">
						Segurança: o Itaú chama o endereço com mTLS (certificado de cliente). Quem valida o certificado é o proxy na frente do backend, que repassa o resultado no cabeçalho definido em PIX_WEBHOOK_MTLS_HEADER (valor esperado em PIX_WEBHOOK_MTLS_VALOR, padrão SUCCESS). Sem essa configuração o receptor recusa todos os avisos. Todo aviso é confirmado no Itaú antes de ser gravado. Acessos recusados aparecem na aba Atividade (Acesso negado).
					</div>
				</v-card-text>
			</v-tab-item>
		</v-tabs-items>

		<v-dialog v-model="dialogDetalhe" max-width="800">
			<v-card v-if="detalhe">
				<v-card-title>
					{{ rotuloTipo(detalhe.tipo) }}
					<v-spacer />
					<v-btn icon @click="dialogDetalhe = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-card-text>
					<div class="text-body-2 mb-2">
						{{ formatarData(detalhe.dtlog) }} | filial {{ detalhe.idempresa === null ? "-" : detalhe.idempresa }} |
						nosso número {{ detalhe.nossonumero || "-" }} | HTTP {{ detalhe.statuscode || "-" }}
					</div>
					<pre class="bloco-codigo">{{ detalhe.resposta || "(sem corpo)" }}</pre>
				</v-card-text>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dialogLigar" width="500">
			<v-card class="pa-5" v-if="alvo">
				<v-card-title class="text-h5"><span class="mx-auto">Ligar o recebimento de Pix</span></v-card-title>
				<v-card-text class="text-h6 text-center">
					A filial {{ alvo.rotulo }} passa a ser consultada {{ status && status.intervalominutos ? "a cada " + status.intervalominutos + " minutos" : "pelo cronjob (hoje desligado)" }}, a partir de
					{{ alvo.dtimplantacao ? formatarDia(alvo.dtimplantacao) : "48 horas atrás" }}. Os Pix de boleto entram na Conciliação como "Aguardando baixa" ou, com a baixa automática da filial ligada, já baixados (só o Pix recebido nas últimas 24 horas).
					<div class="red--text text-body-2 mt-2">Confirme que o período já foi simulado na aba Consulta e testes.</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="green darken-3" dark elevation="0" class="px-8" @click="alterar(alvo, true)">Ligar</v-btn>
					<v-btn color="red darken-3" dark elevation="0" class="px-8" @click="dialogLigar = false">Cancelar</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dialogChave" width="480">
			<v-card class="pa-5" v-if="alvo">
				<v-card-title class="text-h6">Chave Pix da filial {{ alvo.rotulo }}</v-card-title>
				<v-card-text>
					<v-text-field v-model="chaveEditada" label="Chave Pix (CNPJ só com números, e-mail ou chave aleatória)" outlined dense hide-details="auto" />
					<div class="text-caption grey--text mt-2">É a chave usada nos QR Codes dos boletos. Se ficar em branco, vale a que o Itaú informar nos Pix recebidos ou, na falta dela, o CNPJ da filial.</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="dialogChave = false">Cancelar</v-btn>
					<v-btn color="primary" elevation="0" :loading="salvando === alvo.idempresa" @click="salvarChave">Salvar</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dialogGravar" width="480">
			<v-card class="pa-5">
				<v-card-title class="text-h5"><span class="mx-auto">Gravar na Conciliação</span></v-card-title>
				<v-card-text class="text-h6 text-center">
					Isto grava de verdade os Pix de boleto do período na Conciliação da filial {{ filialConsulta }}.
					<div class="red--text text-body-2 mt-2">Simule antes para conferir o que será gravado.</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="green darken-3" dark elevation="0" class="px-8" @click="consultar">Gravar</v-btn>
					<v-btn color="red darken-3" dark elevation="0" class="px-8" @click="dialogGravar = false">Cancelar</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dialogCadastrarWebhook" width="500">
			<v-card class="pa-5">
				<v-card-title class="text-h5"><span class="mx-auto">Cadastrar webhook de Pix</span></v-card-title>
				<v-card-text class="text-h6 text-center">
					Isto cadastra de verdade o nosso endereço na chave Pix da filial {{ filialConsulta }}.
					<div class="red--text text-body-2 mt-2">Consulte antes. Se já houver webhook de outro sistema, o cadastro só é feito marcando a opção abaixo, e o outro sistema deixa de ser avisado.</div>
					<v-checkbox v-model="sobrescreverWebhook" dense hide-details class="d-inline-block mt-2" label="Substituir o webhook existente" />
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="green darken-3" dark elevation="0" class="px-8" @click="cadastrarWebhook">Cadastrar</v-btn>
					<v-btn color="red darken-3" dark elevation="0" class="px-8" @click="dialogCadastrarWebhook = false">Cancelar</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-dialog v-model="dialogRemoverWebhook" width="480">
			<v-card class="pa-5">
				<v-card-title class="text-h5"><span class="mx-auto">Remover webhook de Pix</span></v-card-title>
				<v-card-text class="text-h6 text-center">
					O Itaú deixa de avisar os Pix da filial {{ filialConsulta }} neste endereço. A consulta periódica continua pegando os pagamentos.
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn color="green darken-3" dark elevation="0" class="px-8" @click="removerWebhook">Remover</v-btn>
					<v-btn color="red darken-3" dark elevation="0" class="px-8" @click="dialogRemoverWebhook = false">Cancelar</v-btn>
					<v-spacer />
				</v-card-actions>
			</v-card>
		</v-dialog>

		<v-snackbar v-model="snackbar.ativo" :color="snackbar.cor" timeout="6000">{{ snackbar.texto }}</v-snackbar>
	</v-card>
</template>

<script>
import axios from "axios";
import { mapState } from "vuex";

const ROTULOS = {
	pix_varredura: "Consulta de Pix",
	pix_evento: "Pix de boleto registrado",
	pix_baixa: "Baixa automática do Pix",
	pix_divergencia: "Pix com divergência",
	pix_devolucao: "Pix com devolução",
	pix_erro: "Erro na consulta",
	pix_config: "Configuração do Pix",
	pix_teste: "Teste",
	pix_webhook: "Aviso do webhook de Pix",
	pix_negado: "Acesso negado",
};

const ROTULOS_PIX = { BOLECODE: "Boleto (QR Code)", COB_OUTRO: "Outro QR Code", SEM_TXID: "Transferência" };

function paraCampoData(d) {
	const p = (n) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export default {
	name: "TiPixItau",
	props: {
		filiais: { type: Array, default: () => [] },
	},
	data: () => ({
		aba: 0,
		status: null,
		carregandoStatus: false,
		filialPix: { disponivel: true, lista: [], cnpjs: {} },
		carregandoFilial: false,
		versaoSwitch: 0,
		salvando: null,
		alvo: null,
		dialogLigar: false,
		dialogChave: false,
		chaveEditada: "",
		tipoLog: null,
		filialLog: null,
		limiteLog: 100,
		log: [],
		carregandoLog: false,
		autoAtualizar: false,
		temporizador: null,
		dialogDetalhe: false,
		detalhe: null,
		filialConsulta: null,
		testando: false,
		resultadoTeste: null,
		periodo: { inicio: "", fim: "", simular: true },
		consultando: false,
		dialogGravar: false,
		resultado: null,
		filtroTipo: null,
		consultandoWebhook: false,
		resultadoWebhook: null,
		dialogCadastrarWebhook: false,
		dialogRemoverWebhook: false,
		sobrescreverWebhook: false,
		snackbar: { ativo: false, cor: "success", texto: "" },
		headersLog: [
			{ text: "Data", value: "dtlog" },
			{ text: "Filial", value: "idempresa" },
			{ text: "Tipo", value: "tipo" },
			{ text: "Nosso número", value: "nossonumero" },
			{ text: "Status", value: "statuscode" },
			{ text: "Resposta", value: "resposta", sortable: false },
		],
	}),
	computed: {
		...mapState(["backendUrl"]),
		podeVer() {
			return this.permissao("pix_filial_visualizar") || this.permissao("pix_filial_gerenciar");
		},
		podeGerenciar() {
			return this.permissao("pix_filial_gerenciar");
		},
		itensFiltrados() {
			const itens = (this.resultado && this.resultado.itens) || [];
			return this.filtroTipo ? itens.filter((i) => i.tipo === this.filtroTipo) : itens;
		},
		podeWebhook() {
			return this.permissao("pix_webhook_gerenciar");
		},
		tipoAlertaWebhook() {
			const r = this.resultadoWebhook;
			if (!r) return "info";
			if (r.status === 200) return "warning";
			return r.status === 404 || r.status === 204 ? "success" : "info";
		},
		explicacaoWebhook() {
			const r = this.resultadoWebhook;
			if (!r) return "";
			if (r.mensagem) return r.mensagem;
			if (r.status === 404 || r.status === 204) return "Nenhum webhook cadastrado nesta chave: pode ser cadastrado sem afetar outro sistema.";
			if (r.status === 200) return "Já existe um webhook cadastrado nesta chave. Se não for o nosso, não cadastre sem alinhar com quem o usa.";
			return "Resposta do Itaú.";
		},
		podeExecutar() {
			return this.permissao("pix_varredura_executar");
		},
		filiaisOrdenadas() {
			return [...this.filiais].sort((a, b) => a.idempresa - b.idempresa);
		},
		opcoesFiliais() {
			return this.filiaisOrdenadas.map((f) => ({ text: this.rotuloFilial(f), value: f.idempresa }));
		},
		opcoesFiliaisTodas() {
			return [{ text: "Todas", value: null }, ...this.opcoesFiliais];
		},
		opcoesTipo() {
			return [{ text: "Todos", value: null }, ...Object.keys(ROTULOS).map((t) => ({ text: ROTULOS[t], value: t }))];
		},
		linhasFilial() {
			const estado = {};
			(this.filialPix.lista || []).forEach((l) => {
				estado[l.idempresa] = l;
			});
			return this.filiaisOrdenadas.map((f) => {
				const l = estado[f.idempresa];
				return Object.assign(
					{ idempresa: f.idempresa, rotulo: this.rotuloFilial(f), ativa: false, pix24h: 0, pix7d: 0, bolecode7d: 0, existe: !!l, chavepadrao: (this.filialPix.cnpjs || {})[f.idempresa] || "" },
					l || {}
				);
			});
		},
	},
	methods: {
		carregarTudo() {
			this.carregarStatus();
			this.carregarFilial();
			this.carregarLog();
		},
		carregarStatus() {
			this.carregandoStatus = true;
			return axios
				.post(`${this.backendUrl}pix/status`)
				.then((res) => {
					this.status = res.data;
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível carregar o status do Pix.", "error"))
				.finally(() => {
					this.carregandoStatus = false;
				});
		},
		carregarFilial() {
			this.carregandoFilial = true;
			return axios
				.post(`${this.backendUrl}pix/filial/listar`)
				.then((res) => {
					this.filialPix = { disponivel: !!res.data.disponivel, lista: res.data.lista || [], cnpjs: res.data.cnpjs || {} };
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível carregar as filiais do Pix.", "error"))
				.finally(() => {
					this.carregandoFilial = false;
				});
		},
		pedirAlterar(f, ligar) {
			if (ligar) {
				this.alvo = f;
				this.dialogLigar = true;
				// o switch já mudou na tela; recria o componente pra voltar ao estado real até confirmar
				this.versaoSwitch++;
				return;
			}
			this.alterar(f, false);
		},
		alterar(f, ativa) {
			this.dialogLigar = false;
			this.salvando = f.idempresa;
			return axios
				.post(`${this.backendUrl}pix/filial/salvar`, { idempresa: f.idempresa, ativa, chavepix: f.chavepix || "" })
				.then(() => {
					this.avisar(`Recebimento de Pix da filial ${f.rotulo} ${ativa ? "ligado" : "desligado"}.`, "success");
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível alterar o Pix da filial.", "error"))
				.finally(() => {
					this.salvando = null;
					this.versaoSwitch++;
					this.carregarFilial();
					this.carregarStatus();
				});
		},
		abrirChave(f) {
			this.alvo = f;
			this.chaveEditada = f.chavepix || "";
			this.dialogChave = true;
		},
		salvarChave() {
			const f = this.alvo;
			this.salvando = f.idempresa;
			return axios
				.post(`${this.backendUrl}pix/filial/salvar`, { idempresa: f.idempresa, ativa: !!f.ativa, chavepix: this.chaveEditada })
				.then(() => {
					this.dialogChave = false;
					this.avisar("Chave Pix salva.", "success");
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível salvar a chave.", "error"))
				.finally(() => {
					this.salvando = null;
					this.carregarFilial();
				});
		},
		carregarLog() {
			this.carregandoLog = true;
			return axios
				.post(`${this.backendUrl}pix/log/listar`, {
					tipos: this.tipoLog ? [this.tipoLog] : [],
					idempresa: this.filialLog,
					limite: this.limiteLog,
				})
				.then((res) => {
					this.log = res.data.lista || [];
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível carregar a atividade.", "error"))
				.finally(() => {
					this.carregandoLog = false;
				});
		},
		configurarAutoAtualizar() {
			this.pararTemporizador();
			if (this.autoAtualizar) {
				this.temporizador = setInterval(this.carregarLog, 15000);
			}
		},
		pararTemporizador() {
			if (this.temporizador) {
				clearInterval(this.temporizador);
				this.temporizador = null;
			}
		},
		testar() {
			this.testando = true;
			this.resultadoTeste = null;
			return axios
				.post(`${this.backendUrl}pix/testar`, { idempresa: this.filialConsulta })
				.then((res) => {
					this.resultadoTeste = res.data;
					this.carregarLog();
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível testar o Pix.", "error"))
				.finally(() => {
					this.testando = false;
				});
		},
		pedirConsulta() {
			if (this.periodo.simular) {
				this.consultar();
				return;
			}
			this.dialogGravar = true;
		},
		consultar() {
			this.dialogGravar = false;
			this.consultando = true;
			this.resultado = null;
			this.filtroTipo = null;
			return axios
				.post(`${this.backendUrl}pix/varrer`, {
					idempresa: this.filialConsulta,
					inicio: this.periodo.inicio,
					fim: this.periodo.fim,
					simular: this.periodo.simular,
				})
				.then((res) => {
					this.resultado = res.data;
					this.carregarLog();
					this.carregarFilial();
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível consultar os Pix.", "error"))
				.finally(() => {
					this.consultando = false;
				});
		},
		// clicar de novo no mesmo chip tira o filtro
		filtrarTipo(tipo) {
			this.filtroTipo = this.filtroTipo === tipo ? null : tipo;
		},
		consultarWebhook() {
			this.consultandoWebhook = true;
			this.resultadoWebhook = null;
			return axios
				.post(`${this.backendUrl}pix/webhook/consultar`, { idempresa: this.filialConsulta })
				.then((res) => {
					this.resultadoWebhook = res.data;
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível consultar o webhook de Pix.", "error"))
				.finally(() => {
					this.consultandoWebhook = false;
				});
		},
		cadastrarWebhook() {
			this.dialogCadastrarWebhook = false;
			return axios
				.post(`${this.backendUrl}pix/webhook/cadastrar`, { idempresa: this.filialConsulta, sobrescrever: this.sobrescreverWebhook })
				.then((res) => {
					this.resultadoWebhook = res.data;
					this.avisar("Webhook de Pix cadastrado no Itaú. Consulte para conferir e veja a aba Atividade.", "success");
					this.carregarFilial();
					this.carregarLog();
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível cadastrar o webhook de Pix.", "error"))
				.finally(() => {
					this.sobrescreverWebhook = false;
				});
		},
		removerWebhook() {
			this.dialogRemoverWebhook = false;
			return axios
				.post(`${this.backendUrl}pix/webhook/remover`, { idempresa: this.filialConsulta })
				.then((res) => {
					this.resultadoWebhook = res.data;
					this.avisar("Webhook de Pix removido do Itaú.", "success");
					this.carregarFilial();
					this.carregarLog();
				})
				.catch((e) => this.avisar(this.erroFormatar(e) || "Não foi possível remover o webhook de Pix.", "error"));
		},
		abrirDetalhe(item) {
			this.detalhe = item;
			this.dialogDetalhe = true;
		},
		avisar(texto, cor) {
			this.snackbar = { ativo: true, cor, texto };
		},
		// "1-Ivaipora Matriz": código e nome da filial; sem nome, só o código
		rotuloFilial(f) {
			return f.nome ? `${f.idempresa}-${f.nome}` : `Filial ${f.idempresa}`;
		},
		rotuloTipo(tipo) {
			return ROTULOS[tipo] || tipo;
		},
		rotuloTipoPix(tipo) {
			return ROTULOS_PIX[tipo] || tipo;
		},
		corTipo(tipo) {
			if (tipo === "pix_evento" || tipo === "pix_baixa") return "teal";
			if (tipo === "pix_divergencia" || tipo === "pix_negado") return "orange";
			if (tipo === "pix_erro" || tipo === "pix_devolucao") return "red";
			if (tipo === "pix_teste") return "teal";
			return "blue-grey";
		},
		corStatus(codigo) {
			if (codigo >= 200 && codigo < 300) return "green";
			if (!codigo) return "grey";
			return "red";
		},
		formatarDia(iso) {
			return iso ? iso.split("-").reverse().join("/") : "-";
		},
		formatarData(iso) {
			if (!iso) return "-";
			return new Date(iso).toLocaleString("pt-BR");
		},
	},
	created() {
		const fim = new Date();
		this.periodo.fim = paraCampoData(fim);
		this.periodo.inicio = paraCampoData(new Date(fim.getTime() - 48 * 3600 * 1000));
		if (this.podeVer) this.carregarTudo();
	},
	beforeDestroy() {
		this.pararTemporizador();
	},
};
</script>

<style scoped>
.mono {
	font-family: monospace;
}
.bloco-codigo {
	background: #f5f5f5;
	border-radius: 4px;
	padding: 12px;
	font-size: 12px;
	white-space: pre-wrap;
	word-break: break-word;
	max-height: 360px;
	overflow: auto;
}
.texto-curto {
	display: inline-block;
	max-width: 320px;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	vertical-align: bottom;
}
</style>
